import { useRef, useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import type { KidWithDetails } from '../../../stores/kidStore';
import type { ClasseDuCentre } from '../../../lib/jpa/regles';
import { rangerEnfant, rangerSansClasse } from '../../../lib/jpa/gateway.supabase';
import { signalerClasseManquante } from '../../../lib/classes/gateway.supabase';

/*
  La classe de l'enfant dans l'école choisie : un choix dans la liste des
  classes du centre, ou « Sa classe n'est pas dans la liste ». Le choix
  s'enregistre aussitôt, comme dans la fiche enfant : l'école dans la fiche
  et la classe de l'année (`kid_classes`). L'enfant coché passe alors au
  vert, sans autre bouton à trouver.
*/

interface Props {
  enfant: KidWithDetails;
  ecoleId: string;
  annee: number;
  classes: ClasseDuCentre[];
  classeActuelle: ClasseDuCentre | null;
  /** La question posée au-dessus de la liste. */
  question: string;
  onFait: () => Promise<void>;
  onAnnuler?: () => void;
}

export default function RangerDansUneClasse({
  enfant, ecoleId, annee, classes, classeActuelle, question, onFait, onAnnuler,
}: Props) {
  const [classeId, setClasseId] = useState(classeActuelle?.id ?? '');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /* « Sa classe n'est pas dans la liste » : le nom que le parent lui donne, et l'envoi. */
  const [horsListe, setHorsListe] = useState(false);
  const [classeNommee, setClasseNommee] = useState('');
  const [signalement, setSignalement] = useState<'repos' | 'envoi' | 'echec' | 'fait'>('repos');

  /*
    Un choix à la fois, dans l'ordre : au clavier, chaque flèche change la
    liste. Un choix dépassé par un plus récent n'est pas envoyé ; la base
    garde donc toujours le dernier. La fiche est relue après le dernier
    envoi de la file, même si le parent a remis « Choisir sa classe… » entre
    temps : l'écran montre alors ce que la base a gardé.
  */
  const file = useRef<Promise<void>>(Promise.resolve());
  const voulue = useRef(classeActuelle?.id ?? '');
  const enAttente = useRef(0);

  const choisir = (id: string) => {
    setClasseId(id);
    setErreur(null);
    voulue.current = id;
    if (!id) return;
    enAttente.current += 1;
    setEnCours(true);
    file.current = file.current.then(async () => {
      try {
        if (voulue.current !== id) return;
        await rangerEnfant(enfant.id, ecoleId, enfant.ecole, annee, id);
        if (enAttente.current === 1) await onFait();
      } catch (e) {
        if (voulue.current === id) setErreur(e instanceof Error ? e.message : String(e));
      } finally {
        enAttente.current -= 1;
        if (enAttente.current === 0) setEnCours(false);
      }
    });
  };

  /*
    L'équipe d'abord : si le courriel ne part pas, rien n'a changé dans la
    fiche et le parent peut réessayer. Ensuite seulement, l'école est écrite
    et la classe de l'année retirée, comme dans la fiche enfant ; l'inscription
    attend que la classe soit dans la liste.
  */
  const signaler = async () => {
    const nom = classeNommee.trim();
    if (!nom) return;
    setSignalement('envoi');
    setErreur(null);
    try {
      await signalerClasseManquante({ ecoleId, classe: nom, enfant: { prenom: enfant.prenom, nom: enfant.nom } });
    } catch {
      setSignalement('echec');
      return;
    }
    setSignalement('fait');
    setHorsListe(false);
    try {
      await rangerSansClasse(enfant.id, ecoleId, enfant.ecole, annee);
      await onFait();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    }
  };

  if (signalement === 'fait') {
    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900" role="status">
          <p className="inline-flex items-center gap-1.5 font-semibold">
            <CheckCircle className="h-4 w-4" aria-hidden="true" />
            Classe signalée à l’équipe
          </p>
          <p className="mt-1">
            Nous l’ajoutons à la liste de l’école. Revenez ensuite choisir la classe de {enfant.prenom} pour l’inscrire.
          </p>
        </div>
        {erreur && <p className="text-sm text-red-700" role="alert">{erreur}</p>}
      </div>
    );
  }

  if (horsListe) {
    return (
      <div className="space-y-3">
        <label className="block">
          <span className="form-label">Sa classe, telle que l’école la nomme</span>
          <input
            value={classeNommee}
            onChange={(e) => setClasseNommee(e.target.value)}
            maxLength={120}
            placeholder="P3B — Madame Inès"
            className="form-input min-h-[44px]"
          />
        </label>
        <p className="text-sm text-gray-600">
          Nous prévenons l’équipe, qui l’ajoutera à la liste de l’école. Vous pourrez ensuite inscrire {enfant.prenom}.
        </p>
        {signalement === 'echec' && (
          <p className="text-sm text-red-700" role="alert">Le signalement n’a pas pu partir. Réessayez dans un instant.</p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void signaler()}
            disabled={signalement === 'envoi' || !classeNommee.trim()}
            className="btn-primary inline-flex min-h-[44px] items-center disabled:opacity-50"
          >
            {signalement === 'envoi' && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Prévenir l’équipe
          </button>
          <button type="button" onClick={() => setHorsListe(false)} className="btn-outline min-h-[44px]">
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="block">
        <span className="form-label">{question}</span>
        <select value={classeId} onChange={(e) => choisir(e.target.value)} className="form-input min-h-[48px]">
          <option value="">Choisir sa classe…</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.libelle}</option>)}
        </select>
      </label>
      {enCours && (
        <p className="inline-flex items-center gap-1.5 text-sm text-gray-600" role="status">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Enregistrement…
        </p>
      )}
      {erreur && (
        <p className="text-sm text-red-700" role="alert">
          Sa classe n’a pas pu être enregistrée : {erreur}{' '}
          <button type="button" onClick={() => choisir(classeId)} className="font-semibold underline">Réessayer</button>
        </p>
      )}
      <div className="flex flex-wrap items-center gap-x-4">
        <button
          type="button"
          onClick={() => { setHorsListe(true); setErreur(null); }}
          className="min-h-[44px] text-sm font-semibold text-primary-700 underline"
        >
          Sa classe n’est pas dans la liste
        </button>
        {onAnnuler && (
          <button type="button" onClick={onAnnuler} className="min-h-[44px] text-sm font-semibold text-gray-600">
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}
